import {
  Box,
  Button,
  FormHelperText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  styled,
  tableCellClasses,
} from '@mui/material'
import { CollapsibleSection } from 'components/CollapsibleSection'
import { Loading } from 'components/Loading'
import { formatTimeStampToDate } from 'hooks/date/formattedTime'
import { useEffect, useState } from 'react'
import { Controller, FieldError, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSubmissionQuery } from 'services/submissions'
import { ILot } from 'types/lots'
import { ProcurementEvaluationWorkrateTable } from './procurement-evaluation-workrate-table'
import ConfirmationDialog from 'components/ConfirmationDialog'
import { useBoolean } from 'hooks/useBoolean'
import {
  submissionEvaluationApi,
  useConfirmSubmissionEvaluationMutation,
  useRejectSubmissionEvaluationMutation,
  useSaveSubmissionEvaluationMutation,
  useSubmissionEvaluationQuery,
} from 'services/submissions/submission-evaluation'
import {
  ESubmissionEvaluationStatus,
  ISubmissionEvaluation,
} from 'types/submissions/submissions-evaluation'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { tenderApi } from 'services/tender'
import { RequiredStar } from 'components/RequiredStarSpan'
import { useCreateTechnicalSubmissionMutation } from 'services/submissions/technical-submissions'
import { ITechnicalSubmission } from 'types/submissions'
import { ETenderStatus, ITender } from 'types/tender'
import { DownloadFile } from 'components/DownloadFile'
import { procurementSubmissionApi } from 'services/submissions/procurement-submission'
import { procurementsApi } from 'services/procurements'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}))

const StyledTypography = styled(Typography)({
  fontWeight: '600',
  lineHeight: '20px',
  marginBottom: '15px',
})

function mapFormDataToObject(
  obj: ITechnicalSubmission,
  formData: { evaluationScore: string; evaluationNote: string }[]
) {
  const resultObj = { ...obj }

  resultObj.technicalSubmissionItems = resultObj.technicalSubmissionItems.map(
    (item, index) => {
      const formItem = formData[index]
      return {
        ...item,
        evaluationNote:
          formItem.evaluationNote !== undefined
            ? formItem.evaluationNote
            : item.evaluationNote,
        evaluationScore:
          formItem.evaluationScore !== undefined
            ? formItem.evaluationScore
            : item.evaluationScore,
      }
    }
  )

  return resultObj
}

interface FormErrors {
  offerPriceNotes?: string
  offerDurationNotes?: string
  offerGuaranteeNotes?: string
  offerGuaranteeDurationNotes?: string
  offerAgreementExecutionGuaranteeNotes?: string
  calculatedScore?: string
  technicalSubmission?: {
    technicalSubmissionItems?: {
      evaluationNote?: {
        message?: string
      }
      evaluationScore?: {
        message?: string
      }
    }[]
  }
}

type Props = {
  subEvaListItem: ISubmissionEvaluation
  lot: ILot
  tender: ITender
}

const TenderEvaluationSubmissions = ({ subEvaListItem, lot, tender }: Props) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<boolean>(false)
  const dialogRejectBidder = useBoolean(false)
  const dispatch = useDispatch()

  const {
    data: submission,
    isLoading,
    isError,
    refetch,
  } = useSubmissionQuery({ submissionId: subEvaListItem.submissionId })

  const {
    data: submissionEvaluation,
    isLoading: isSubEvaLoading,
    isError: isSubEvaError,
    refetch: refetchSubmissionEvaluation,
  } = useSubmissionEvaluationQuery(
    { id: subEvaListItem.submissionEvaluationId },
    { skip: !expanded }
  )

  const [
    rejectSubmission,
    {
      isLoading: isRejectSubmissionLoading,
      isError: isRejectsubmissionError,
      isSuccess: isRejectSubSuccess,
    },
  ] = useRejectSubmissionEvaluationMutation()

  const [
    confirmSubmission,
    {
      isLoading: isConfirmSubmissionLoading,
      isError: isConfirmSubmissionError,
      isSuccess: isConfirmSubSuccess,
    },
  ] = useConfirmSubmissionEvaluationMutation()

  const handleChangeAccordion = (expanded: boolean) => {
    setExpanded(expanded)
  }

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    getValues,
    setValue,
  } = useForm<any>({
    defaultValues: {
      offerPriceNotes: submissionEvaluation?.offerPriceNotes || '',
      offerDurationNotes: submissionEvaluation?.offerDurationNotes || '',
      offerGuaranteeNotes: submissionEvaluation?.offerGuaranteeNotes || '',
      offerGuaranteeDurationNotes:
        submissionEvaluation?.offerGuaranteeDurationNotes || '',
      offerAgreementExecutionGuaranteeNotes:
        submissionEvaluation?.offerAgreementExecutionGuaranteeNotes || '',
      calculatedScore: submissionEvaluation?.calculatedScore || 0,
      technicalSubmission: (submission?.technicalSubmission as any) || {},
    },
  })

  useEffect(() => {
    if (submissionEvaluation) {
      setValue('offerPriceNotes', submissionEvaluation.offerPriceNotes)
      setValue('offerDurationNotes', submissionEvaluation.offerDurationNotes)
      setValue('offerGuaranteeNotes', submissionEvaluation.offerGuaranteeNotes)
      setValue(
        'offerGuaranteeDurationNotes',
        submissionEvaluation.offerGuaranteeDurationNotes
      )
      setValue(
        'offerAgreementExecutionGuaranteeNotes',
        submissionEvaluation.offerAgreementExecutionGuaranteeNotes
      )
      setValue('calculatedScore', submissionEvaluation.calculatedScore)
      setValue('technicalSubmission', submission?.technicalSubmission)
    }
  }, [submissionEvaluation])

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'technicalSubmission',
  })

  const [
    createTechnicalSubmission,
    { isSuccess: isCreateTechSubSuccess, isError: isCreateTechSubError },
  ] = useCreateTechnicalSubmissionMutation()

  const [
    saveSubmissionEvaluation,
    {
      isSuccess: isSaveSubmisssionEvaluationSuccesss,
      isError: isSaveSubmissionEvaluationError,
      isLoading: isSaveSubmissionEvaluationLoading,
    },
  ] = useSaveSubmissionEvaluationMutation()

  useEffect(() => {
    if (isCreateTechSubSuccess || isSaveSubmisssionEvaluationSuccesss) {
      toast.success(t('operation-success'))
    }
    if (isCreateTechSubError || isSaveSubmissionEvaluationError) {
      toast.error(t('failedToCreate'))
    }
  }, [
    isCreateTechSubSuccess,
    isCreateTechSubError,
    isSaveSubmisssionEvaluationSuccesss,
    isSaveSubmissionEvaluationError,
    t,
  ])

  const handleSave = async () => {
    const body = {
      ...getValues(),
      evaluationId: subEvaListItem.submissionEvaluationId,
    }

    const technicalSub = submission?.technicalSubmission!
    const bodyTechnical = await mapFormDataToObject(
      technicalSub,
      getValues().technicalSubmission.technicalSubmissionItems
    )
    await createTechnicalSubmission(bodyTechnical)

    saveSubmissionEvaluation(body)
    refetch()
    refetchSubmissionEvaluation()
  }

  const onSubmit = async (values: any) => {
    const technicalSub = submission?.technicalSubmission!
    const body = await mapFormDataToObject(
      technicalSub,
      values.technicalSubmission.technicalSubmissionItems
    )
    await createTechnicalSubmission(body)
    await confirmSubmission({
      ...values,
      submissionEvaluationId: subEvaListItem.submissionEvaluationId,
    })
    dispatch(submissionEvaluationApi.util.invalidateTags(['SubmissionEvaluations']))
    dispatch(tenderApi.util.invalidateTags(['TenderById']))
    dispatch(
      procurementSubmissionApi.util.invalidateTags([
        'ProcurementSubmissionsItems',
        'ProcurementSubmissions',
      ])
    )
    dispatch(procurementsApi.util.invalidateTags(['ProcurementItems']))
    refetch()
    refetchSubmissionEvaluation()
  }

  const handleRejectSubmission = async () => {
    await rejectSubmission({
      submissionEvaluationId: subEvaListItem.submissionEvaluationId,
    })
    dispatch(submissionEvaluationApi.util.invalidateTags(['SubmissionEvaluations']))
    dispatch(tenderApi.util.invalidateTags(['TenderById']))
  }

  useEffect(() => {
    if (isConfirmSubSuccess || isRejectSubSuccess) {
      toast.success(t('operation-success'))
    }

    if (isError || isRejectsubmissionError || isConfirmSubmissionError) {
      toast.success(t('operation-failure'))
    }
  }, [
    isConfirmSubSuccess,
    isRejectSubSuccess,
    isRejectsubmissionError,
    isConfirmSubmissionError,
    isError,
    t,
  ])

  return (
    <CollapsibleSection
      variant={
        subEvaListItem.status === ESubmissionEvaluationStatus.REJECTED
          ? 'error'
          : subEvaListItem.status === ESubmissionEvaluationStatus.CONFIRMED
            ? 'success'
            : 'secondary'
      }
      title={
        subEvaListItem.submitterOrganizationName
          ? `${subEvaListItem.submitterOrganizationName} - ${t('final-price')}: ${subEvaListItem.totalPrice}`
          : ''
      }
      additionalTitle={
        <Box sx={{ display: 'flex', gap: '20px', marginRight: '30px' }}>
          <Typography>
            {t('status')} -{' '}
            {subEvaListItem.status
              ? t(
                  `submission-evaluation-status-${ESubmissionEvaluationStatus[subEvaListItem.status]}`
                )
              : ''}
          </Typography>
          <Typography>
            {t('submission-presentation-date')} -{' '}
            {formatTimeStampToDate(subEvaListItem.submissionCreatedDate)}
          </Typography>
        </Box>
      }
      defaultExpanded={false}
      onChange={(event: React.SyntheticEvent, expanded: boolean) => {
        handleChangeAccordion(expanded)
      }}
    >
      <ConfirmationDialog
        open={dialogRejectBidder.isTrue}
        onClose={dialogRejectBidder.setFalse}
        onConfirm={handleRejectSubmission}
        text={t('reject-bidder-info')}
      />
      {isLoading ||
      isRejectSubmissionLoading ||
      isConfirmSubmissionLoading ||
      isSaveSubmissionEvaluationLoading ? (
        <Loading fullPage={false} />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} id="evaluation-form">
          <Stack spacing={5}>
            <Stack spacing={1}>
              <Stack>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, marginBottom: '20px' }}
                >
                  {t('price-proposal')}
                </Typography>
                <TableContainer>
                  <Table
                    sx={{
                      tableLayout: 'fixed',
                      '& td, th': {
                        border: '1px solid #ccc',
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>
                          {t('evaluation-of-organization-docs')}
                        </StyledTableCell>
                        <StyledTableCell>
                          {t('evaluation-of-contractor-docs')}
                        </StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <StyledTypography>
                            {t('tender-proposal-price-description')}
                          </StyledTypography>
                          <Table>
                            <TableBody>
                              <TableRow>
                                <TableCell>
                                  <Stack spacing={3}>
                                    <TextField
                                      defaultValue={lot.procurementBidWeight}
                                      disabled
                                      label={t('tender-proposal-general-weight')}
                                      fullWidth
                                      InputLabelProps={{ shrink: true }}
                                    />
                                    <TextField
                                      disabled
                                      defaultValue={
                                        submission?.procurementSubmission
                                          ?.procurementRequirement?.priceDescription
                                      }
                                      fullWidth
                                      label={t('evaluationMethod')}
                                      InputLabelProps={{ shrink: true }}
                                      InputProps={{
                                        rows: 2,
                                        multiline: true,
                                        inputComponent: 'textarea',
                                      }}
                                    />
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Stack spacing={3}>
                                    <Controller
                                      name="calculatedScore"
                                      control={control}
                                      render={({ field }) => (
                                        <TextField
                                          {...field}
                                          label={t('portal-point')}
                                          fullWidth
                                        />
                                      )}
                                    />
                                    <Controller
                                      name="offerPriceNotes"
                                      control={control}
                                      defaultValue={
                                        submissionEvaluation?.offerPriceNotes
                                      }
                                      render={({ field }) => (
                                        <TextField
                                          {...field}
                                          fullWidth
                                          label={t('notes-about-evaluation')}
                                          InputProps={{
                                            rows: 2,
                                            multiline: true,
                                            inputComponent: 'textarea',
                                          }}
                                        />
                                      )}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <Box>
                            <StyledTypography>
                              {t('tender-proposal-final-price')}
                            </StyledTypography>
                            <TextField
                              disabled
                              fullWidth
                              defaultValue={subEvaListItem.totalPrice}
                              InputProps={{
                                rows: 2,
                                multiline: true,
                                inputComponent: 'textarea',
                              }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Box>
                          <Box sx={{ marginTop: '24px' }}>
                            <TextField
                              label={t('comment')}
                              disabled
                              fullWidth
                              defaultValue={
                                submission?.procurementSubmission
                                  ?.commentOnOfferValue
                              }
                              InputProps={{
                                rows: 2,
                                multiline: true,
                                inputComponent: 'textarea',
                              }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <StyledTypography>
                            {t('tender-proposal-expiration-duration')}
                          </StyledTypography>
                          <Table>
                            <TableBody>
                              <TableRow>
                                <TableCell>
                                  <Stack spacing={3}>
                                    <TextField
                                      defaultValue={
                                        submission?.procurementSubmission
                                          ?.procurementRequirement
                                          ?.validityDurationDays
                                      }
                                      disabled
                                      fullWidth
                                      label={t('evaluationMethod')}
                                      InputProps={{
                                        rows: 2,
                                        multiline: true,
                                        inputComponent: 'textarea',
                                      }}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Stack spacing={3}>
                                    <Controller
                                      name="offerDurationNotes"
                                      control={control}
                                      defaultValue={
                                        submissionEvaluation?.offerDurationNotes
                                      }
                                      render={({ field }) => (
                                        <TextField
                                          {...field}
                                          fullWidth
                                          label={t('notes-about-evaluation')}
                                          InputProps={{
                                            rows: 2,
                                            multiline: true,
                                            inputComponent: 'textarea',
                                          }}
                                        />
                                      )}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <StyledTypography>
                            {t('tender-proposal-expiration-duration')}
                          </StyledTypography>
                          <TextField
                            defaultValue={
                              submission?.procurementSubmission
                                ?.procurementRequirement?.validityDurationDays
                            }
                            disabled
                            fullWidth
                            InputProps={{
                              rows: 2,
                              multiline: true,
                              inputComponent: 'textarea',
                            }}
                            InputLabelProps={{ shrink: true }}
                          />
                          <DownloadFile
                            fileId={
                              submission?.procurementSubmission
                                ?.procurementRequirement?.extraFileId
                            }
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <StyledTypography>
                            {t('tender-proposal-warranty-amount')}
                          </StyledTypography>
                          <Table>
                            <TableBody>
                              <TableRow>
                                <TableCell>
                                  <Stack spacing={3}>
                                    <TextField
                                      defaultValue={
                                        submission?.procurementSubmission
                                          ?.procurementRequirement?.guaranteeAmount
                                      }
                                      disabled
                                      label={t('evaluationMethod')}
                                      fullWidth
                                      InputProps={{
                                        rows: 2,
                                        multiline: true,
                                        inputComponent: 'textarea',
                                      }}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Stack spacing={3}>
                                    <Controller
                                      name="offerGuaranteeNotes"
                                      control={control}
                                      defaultValue={
                                        submissionEvaluation?.offerGuaranteeNotes
                                      }
                                      render={({ field }) => (
                                        <TextField
                                          {...field}
                                          fullWidth
                                          label={t('notes-about-evaluation')}
                                          InputProps={{
                                            rows: 2,
                                            multiline: true,
                                            inputComponent: 'textarea',
                                          }}
                                        />
                                      )}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <StyledTypography>
                            {t('tender-proposal-warranty-amount')}
                          </StyledTypography>
                          <TextField
                            defaultValue={
                              submission?.procurementSubmission
                                ?.procurementRequirement?.validityDurationDays
                            }
                            disabled
                            fullWidth
                            InputProps={{
                              rows: 2,
                              multiline: true,
                              inputComponent: 'textarea',
                            }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <StyledTypography>
                            {t('tender-proposal-validity-duration')}
                          </StyledTypography>
                          <Table>
                            <TableBody>
                              <TableRow>
                                <TableCell>
                                  <Stack spacing={3}>
                                    <TextField
                                      defaultValue={
                                        submission?.procurementSubmission
                                          ?.procurementRequirement
                                          ?.guaranteeValidityDurationDays
                                      }
                                      disabled
                                      fullWidth
                                      label={t('evaluationMethod')}
                                      InputProps={{
                                        rows: 2,
                                        multiline: true,
                                        inputComponent: 'textarea',
                                      }}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Stack spacing={3}>
                                    <Controller
                                      name="offerGuaranteeDurationNotes"
                                      control={control}
                                      defaultValue={
                                        submissionEvaluation?.offerGuaranteeDurationNotes
                                      }
                                      render={({ field }) => (
                                        <TextField
                                          {...field}
                                          fullWidth
                                          label={t('notes-about-evaluation')}
                                          InputProps={{
                                            rows: 2,
                                            multiline: true,
                                            inputComponent: 'textarea',
                                          }}
                                        />
                                      )}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <StyledTypography>
                            {t('tender-proposal-validity-duration')}
                          </StyledTypography>
                          <TextField
                            defaultValue={
                              submission?.procurementSubmission
                                ?.procurementRequirement
                                ?.guaranteeValidityDurationDays
                            }
                            disabled
                            fullWidth
                            InputProps={{
                              rows: 2,
                              multiline: true,
                              inputComponent: 'textarea',
                            }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <StyledTypography>
                            {t('warranty-of-procurement-contract-action')}
                          </StyledTypography>
                          <Table>
                            <TableBody>
                              <TableRow>
                                <TableCell>
                                  <Stack spacing={3}>
                                    <TextField
                                      defaultValue={
                                        submission?.procurementSubmission
                                          ?.procurementRequirement
                                          ?.executionGuarantee
                                      }
                                      disabled
                                      fullWidth
                                      label={t('evaluationMethod')}
                                      InputProps={{
                                        rows: 2,
                                        multiline: true,
                                        inputComponent: 'textarea',
                                      }}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Stack spacing={3}>
                                    <Controller
                                      name="offerAgreementExecutionGuaranteeNotes"
                                      control={control}
                                      defaultValue={
                                        submissionEvaluation?.offerAgreementExecutionGuaranteeNotes
                                      }
                                      render={({ field }) => (
                                        <TextField
                                          {...field}
                                          fullWidth
                                          label={t('notes-about-evaluation')}
                                          InputProps={{
                                            rows: 2,
                                            multiline: true,
                                            inputComponent: 'textarea',
                                          }}
                                        />
                                      )}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <StyledTypography>
                            {t('warranty-of-procurement-contract-action')}
                          </StyledTypography>
                          <TextField
                            defaultValue={
                              submission?.procurementSubmission
                                ?.procurementRequirement?.executionGuarantee
                            }
                            disabled
                            fullWidth
                            InputProps={{
                              rows: 2,
                              multiline: true,
                              inputComponent: 'textarea',
                            }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Stack>
            <ProcurementEvaluationWorkrateTable
              submission={submission}
              procurementRequirementId={lot.procurementRequirement.id}
            />
            {/* texniki teklifi */}
            {submission && (
              <Stack spacing={1}>
                <Stack>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, marginBottom: '20px' }}
                  >
                    {t('technical-proposal')}
                  </Typography>
                  <TableContainer>
                    {submission.technicalSubmission?.technicalSubmissionItems?.map(
                      (sub, index) => {
                        return (
                          <Table
                            key={sub.id}
                            sx={{
                              tableLayout: 'fixed',
                              '& td, th': {
                                border: '1px solid #ccc',
                              },
                            }}
                          >
                            <TableHead>
                              <TableRow>
                                <StyledTableCell>
                                  {t('evaluationOfDocuments')}
                                </StyledTableCell>
                                <StyledTableCell>
                                  {t('contractorOfferAndNotes')}
                                </StyledTableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow key={sub.id}>
                                <TableCell>
                                  <StyledTypography>
                                    {t('tender-proposal-price-description')}
                                  </StyledTypography>
                                  <Table>
                                    <TableBody>
                                      <TableRow>
                                        <TableCell>
                                          <Stack spacing={3}>
                                            <TextField
                                              disabled
                                              label={t('criteria-name')}
                                              fullWidth
                                              defaultValue={
                                                sub.technicalRequirementItem
                                                  ?.criteria
                                              }
                                              InputLabelProps={{ shrink: true }}
                                            />
                                            <TextField
                                              disabled
                                              label={t('criteria-point')}
                                              fullWidth
                                              defaultValue={
                                                sub.technicalRequirementItem?.point
                                              }
                                              InputLabelProps={{ shrink: true }}
                                            />
                                            <TextField
                                              defaultValue={
                                                sub.technicalRequirementItem
                                                  ?.eligibility
                                              }
                                              disabled
                                              fullWidth
                                              label={t('evaluationMethod')}
                                              InputProps={{
                                                rows: 2,
                                                multiline: true,
                                                inputComponent: 'textarea',
                                              }}
                                              InputLabelProps={{ shrink: true }}
                                            />
                                            <Controller
                                              name={`technicalSubmission.technicalSubmissionItems.${index}.evaluationScore`}
                                              rules={{ required: t('required') }}
                                              defaultValue={
                                                submission.technicalSubmission
                                                  .technicalSubmissionItems[index]
                                                  .evaluationScore
                                              }
                                              control={control}
                                              render={({ field }) => (
                                                <TextField
                                                  {...field}
                                                  fullWidth
                                                  label={
                                                    <>
                                                      {t('evaluation-score')}
                                                      <RequiredStar>*</RequiredStar>
                                                    </>
                                                  }
                                                />
                                              )}
                                            />
                                            {(errors as FormErrors)
                                              ?.technicalSubmission
                                              ?.technicalSubmissionItems?.[index]
                                              ?.evaluationScore && (
                                              <FormHelperText error>
                                                {(errors as FormErrors)
                                                  ?.technicalSubmission
                                                  ?.technicalSubmissionItems?.[index]
                                                  ?.evaluationScore?.message ||
                                                  t('required')}
                                              </FormHelperText>
                                            )}
                                            <Controller
                                              name={`technicalSubmission.technicalSubmissionItems.${index}.evaluationNote`}
                                              control={control}
                                              rules={{ required: t('required') }}
                                              defaultValue={
                                                submission.technicalSubmission
                                                  .technicalSubmissionItems[index]
                                                  .evaluationNote
                                              }
                                              render={({ field }) => (
                                                <>
                                                  <TextField
                                                    {...field}
                                                    fullWidth
                                                    label={
                                                      <>
                                                        {t('notes-about-evaluation')}
                                                        <RequiredStar>
                                                          *
                                                        </RequiredStar>
                                                      </>
                                                    }
                                                    InputProps={{
                                                      rows: 2,
                                                      multiline: true,
                                                      inputComponent: 'textarea',
                                                    }}
                                                  />
                                                </>
                                              )}
                                            />
                                            {(errors as FormErrors)
                                              ?.technicalSubmission
                                              ?.technicalSubmissionItems?.[index]
                                              ?.evaluationNote && (
                                              <FormHelperText error>
                                                {(errors as FormErrors)
                                                  ?.technicalSubmission
                                                  ?.technicalSubmissionItems?.[index]
                                                  ?.evaluationNote?.message ||
                                                  t('required')}
                                              </FormHelperText>
                                            )}
                                          </Stack>
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>
                                  <StyledTypography>
                                    {t('tender-proposal-final-price')}
                                  </StyledTypography>
                                  <Stack spacing={3}>
                                    <TextField
                                      defaultValue={sub.reply}
                                      disabled
                                      fullWidth
                                      label={t('answer')}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                    <TextField
                                      defaultValue={sub.note}
                                      disabled
                                      fullWidth
                                      label={t('contractor-notes')}
                                      InputProps={{
                                        rows: 2,
                                        multiline: true,
                                        inputComponent: 'textarea',
                                      }}
                                      InputLabelProps={{ shrink: true }}
                                    />

                                    <Controller
                                      name={`technicalSubmission.technicalSubmissionItems.${index}.submittedDocumentFileId`}
                                      control={control}
                                      render={({ field }) => (
                                        <DownloadFile
                                          fileId={
                                            submission.technicalSubmission
                                              .technicalSubmissionItems[index]
                                              .submittedDocumentFileId
                                          }
                                        />
                                      )}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        )
                      }
                    )}
                  </TableContainer>
                </Stack>
              </Stack>
            )}
          </Stack>
          {tender.status !== ETenderStatus.COMMISSION_EVALUATION && (
            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1}
              sx={{ marginTop: '20px' }}
            >
              <Button
                size="small"
                variant="contained"
                style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                onClick={handleSave}
                disabled={!isValid}
              >
                {t('save')}
              </Button>
              <Button
                size="small"
                variant="contained"
                style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                sx={{
                  backgroundColor: '#f14433',
                  '&:hover': { background: '#f14433' },
                }}
                onClick={dialogRejectBidder.setTrue}
              >
                {t('reject-bidder')}
              </Button>
              <Button
                size="small"
                variant="contained"
                style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                form="evaluation-form"
                type="submit"
                disabled={!isValid}
                onClick={handleSubmit(onSubmit)}
              >
                {t('confirm')}
              </Button>
            </Stack>
          )}
        </form>
      )}
    </CollapsibleSection>
  )
}

export default TenderEvaluationSubmissions
